<script setup lang="ts">
import { ref, onMounted, watch } from 'vue';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { TabsContent, TabsList, TabsRoot, TabsTrigger } from 'reka-ui';
import { FileText } from '@lucide/vue';
import type { Document } from '@/types/ui/PromptManager';
import { aiService } from '@/services/AIService';
import PlatformDocumentSelector from '@/components/ui/document/PlatformDocumentSelector.vue';
import PlaygroundDocumentSelector from '@/components/ui/document/PlaygroundDocumentSelector.vue';

interface Props {
  selectedDocument: Document | null;
}

const props = defineProps<Props>();

const emit = defineEmits<{
  documentSelect: [document: Document | null];
}>();

const currentTab = ref<string>('platform');
const uploadDocuments = ref<Document[]>([]);

// When a playground document (s3Key) is pre-selected, switch to the upload tab
watch(
  () => props.selectedDocument,
  (doc) => {
    if (doc?.s3Key && !doc.accountId) {
      currentTab.value = 'upload';
    }
  },
  { immediate: true },
);

const handleDocumentSelect = (document: Document | null) => {
  emit('documentSelect', document);
};

const handleUploadDocumentsUpdate = (documents: Document[]) => {
  uploadDocuments.value = documents;
};

// Pre-fetch upload documents on mount
onMounted(async () => {
  try {
    uploadDocuments.value = await aiService.getAllDocuments();
  } catch (error) {
    console.error('Failed to pre-fetch upload documents:', error);
  }
});
</script>

<template>
  <Card class="h-fit border-slate-200 bg-white/80 backdrop-blur-sm">
    <CardHeader>
      <CardTitle class="flex items-center gap-2">
        <FileText class="h-5 w-5 text-blue-600" />
        Documents
      </CardTitle>
      <CardDescription> Select a document for processing </CardDescription>
    </CardHeader>
    <CardContent>
      <TabsRoot
        v-model="currentTab"
        class="w-full">
        <TabsList class="inline-flex h-10 w-fit items-center justify-center rounded-md bg-slate-100 p-1 text-slate-600">
          <TabsTrigger
            value="platform"
            class="inline-flex items-center justify-center rounded-sm px-3 py-1.5 text-sm font-medium whitespace-nowrap ring-offset-white transition-all focus-visible:ring-2 focus-visible:ring-slate-950 focus-visible:ring-offset-2 focus-visible:outline-none disabled:pointer-events-none disabled:opacity-50 data-[state=active]:bg-white data-[state=active]:text-slate-950 data-[state=active]:shadow-xs">
            Platform Documents
          </TabsTrigger>
          <TabsTrigger
            value="upload"
            class="inline-flex items-center justify-center rounded-sm px-3 py-1.5 text-sm font-medium whitespace-nowrap ring-offset-white transition-all focus-visible:ring-2 focus-visible:ring-slate-950 focus-visible:ring-offset-2 focus-visible:outline-none disabled:pointer-events-none disabled:opacity-50 data-[state=active]:bg-white data-[state=active]:text-slate-950 data-[state=active]:shadow-xs">
            Playground Documents
          </TabsTrigger>
        </TabsList>

        <!-- Platform Documents Tab Content -->
        <TabsContent
          value="platform"
          class="mt-4">
          <PlatformDocumentSelector
            :selectedDocument="selectedDocument"
            @documentSelect="handleDocumentSelect" />
        </TabsContent>

        <!-- Upload Documents Tab Content -->
        <TabsContent
          value="upload"
          class="mt-4">
          <PlaygroundDocumentSelector
            :selectedDocument="selectedDocument"
            :documents="uploadDocuments"
            @documentSelect="handleDocumentSelect"
            @update:documents="handleUploadDocumentsUpdate" />
        </TabsContent>
      </TabsRoot>
    </CardContent>
  </Card>
</template>
